<template>
  <kv-modal ref="modal" size="large" @close="onHide">
    <template #title>
      {{ modalTitle }}
    </template>
    <template #body>
      <div class="create-task-modal">
        <div class="create-task-client" v-if="!lockClient">
          <clients-field v-model="form.clients_id" />
        </div>
        <kv-chip
          v-else-if="lockedClientLabel"
          size="small"
          density="compact"
          rounded="pill"
          color="muted"
          prepend-icon="ui-user-01"
          class="create-task-client-locked"
        >
          {{ lockedClientLabel }}
        </kv-chip>

        <div class="create-task-field">
          <kv-input
            v-model="form.title"
            size="regular"
            variant="outlined"
            :placeholder="t('tasks-title_placeholder')"
            :class="{ 'kv-error': !!titleError }"
            @input="titleError = ''"
          />
          <p v-if="titleError" class="create-task-error">
            {{ titleError }}
          </p>
        </div>

        <div class="create-task-description">
          <benefits-rich-text
            v-model="form.description"
            :placeholder="t('tasks-description_placeholder')"
            height="140px"
            variant="outlined"
            size="regular"
          />
        </div>

        <div class="create-task-toolbar">
          <div class="create-task-actions">
            <kv-popup placement="bottom left" min-width="240px">
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  class="create-task-chip-btn"
                  :title="assigneeLabel"
                  @click="toggle"
                >
                  <span v-if="selectedAssignee" class="create-task-avatar">
                    {{ initials(selectedAssignee.name) }}
                  </span>
                  <Icon v-else icon-class="ui-user-01" size="3" />
                </button>
              </template>
              <template #content="{ toggle }">
                <div class="create-task-menu">
                  <kv-typography
                    size="extra-small"
                    weight="medium"
                    color="muted"
                    class="create-task-menu-label"
                  >
                    {{ t("tasks-col_assignee") }}
                  </kv-typography>
                  <button
                    type="button"
                    class="create-task-menu-item"
                    @click="form.assignee_id = ''; toggle(false)"
                  >
                    {{ t("tasks-none") }}
                  </button>
                  <button
                    v-for="user in canAssignTo"
                    :key="user.id"
                    type="button"
                    class="create-task-menu-item"
                    :class="{
                      active: String(user.id) === String(form.assignee_id),
                    }"
                    @click="
                      form.assignee_id = String(user.id);
                      toggle(false);
                    "
                  >
                    <span class="create-task-avatar mini">{{
                      initials(user.name)
                    }}</span>
                    <span>{{ user.name }}</span>
                  </button>
                </div>
              </template>
            </kv-popup>

            <kv-popup placement="bottom left" min-width="180px">
              <template #trigger="{ toggle }">
                <button
                  type="button"
                  class="create-task-chip-btn"
                  :class="'tone-' + priorityTone(form.priority)"
                  :title="priorityLabel"
                  @click="toggle"
                >
                  <Icon icon-class="ui-flag-01" size="3" />
                </button>
              </template>
              <template #content="{ toggle }">
                <div class="create-task-menu">
                  <kv-typography
                    size="extra-small"
                    weight="medium"
                    color="muted"
                    class="create-task-menu-label"
                  >
                    {{ t("tasks-priority") }}
                  </kv-typography>
                  <button
                    v-for="opt in priorityOptions"
                    :key="opt.value"
                    type="button"
                    class="create-task-menu-item"
                    :class="'tone-' + opt.tone"
                    @click="
                      form.priority = opt.value;
                      toggle(false);
                    "
                  >
                    <Icon
                      :icon-class="
                        opt.value === 0 ? 'ui-x-close' : 'ui-flag-01'
                      "
                      size="3"
                    />
                    <span>{{ opt.label }}</span>
                  </button>
                </div>
              </template>
            </kv-popup>

            <kv-date-picker
              v-model="form.due_date"
              value-format="yyyy-MM-dd"
              size="small"
              variant="outlined"
              :placeholder="t('tasks-no_date')"
              :clearable="true"
              placement="bottom left"
            >
              <template #trigger>
                <button
                  type="button"
                  class="create-task-chip-btn"
                  :class="{ 'has-date': !!form.due_date }"
                  :title="dueDateLabel"
                >
                  <Icon icon-class="ui-calendar" size="3" />
                </button>
              </template>
            </kv-date-picker>
          </div>

          <div class="create-task-board-row">
            <kv-select
              v-model="form.tasks_boards_id"
              class="create-task-board-select"
              :options="boardOptions"
              value-field="value"
              label-field="label"
              size="small"
              variant="outlined"
              filterable
              :placeholder="t('tasks-select_board')"
              @input="onBoardChange"
            />
            <kv-select
              v-model="form.tasks_boards_columns_id"
              class="create-task-board-select"
              :options="stageOptions"
              value-field="value"
              label-field="label"
              size="small"
              variant="outlined"
              filterable
              :disabled="!hasSelectedBoard"
              :placeholder="t('tasks-create_task_stage_placeholder')"
            />
          </div>
        </div>
      </div>
    </template>
    <template #footer="{ toggle }">
      <div class="create-task-footer">
        <kv-switch
          id="create-task-create-more"
          v-model="createMore"
          :label="t('tasks-create_more')"
        />
        <div class="create-task-footer-actions">
          <kv-button variant="outlined" size="small" @click="toggle">
            {{ t("tasks-cancel") }}
          </kv-button>
          <kv-button
            variant="filled"
            size="small"
            prepend-icon="ui-plus"
            :loading="saving"
            :disabled="saving"
            @click="save(toggle)"
          >
            {{ t("tasks-create_task_action") }}
          </kv-button>
        </div>
      </div>
    </template>
  </kv-modal>
</template>

<script>
import { mapState } from "vuex";
import keviApi from "../keviApi";
import Icon from "../components/icon/icon";
import KvModal from "../components/modal/modal.vue";
import KvButton from "../components/form/kv-button.vue";
import KvInput from "../components/inputs/kv-input.vue";
import KvSelect from "../components/inputs/kv-select.vue";
import KvDatePicker from "../components/inputs/kv-date-picker.vue";
import KvPopup from "../components/ui/kv-popup.vue";
import KvTypography from "../components/ui/kv-typography.vue";
import KvChip from "../components/ui/kv-chip.vue";
import KvSwitch from "../components/inputs/kv-switch.vue";
import ClientsField from "../clients/clients-field";
import BenefitsRichText from "../benefits/shared/benefits-rich-text.vue";

const NO_BOARD_VALUE = "none";

const emptyForm = () => ({
  title: "",
  description: "",
  tasks_boards_id: NO_BOARD_VALUE,
  tasks_boards_columns_id: "",
  assignee_id: "",
  due_date: null,
  clients_id: "",
  answers_id: null,
  priority: 0,
});

export default {
  name: "CreateTaskModal",
  components: {
    Icon,
    "kv-modal": KvModal,
    "kv-button": KvButton,
    "kv-input": KvInput,
    "kv-select": KvSelect,
    "kv-date-picker": KvDatePicker,
    "kv-popup": KvPopup,
    "kv-typography": KvTypography,
    "kv-chip": KvChip,
    "kv-switch": KvSwitch,
    "clients-field": ClientsField,
    "benefits-rich-text": BenefitsRichText,
  },
  data() {
    return {
      form: emptyForm(),
      boards: [],
      canAssignTo: [],
      saving: false,
      createMore: false,
      titleError: "",
      lockClient: false,
      lockedClientLabel: "",
      customModalTitle: "",
      opened: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user,
      place: (state) => state.place,
    }),
    modalTitle() {
      return this.customModalTitle || this.t("tasks-create_task_title");
    },
    boardOptions() {
      return [
        { value: NO_BOARD_VALUE, label: this.t("tasks-no_board_option") },
        ...(this.boards || []).map((board) => ({
          value: String(board.id),
          label: board.name,
        })),
      ];
    },
    hasSelectedBoard() {
      return (
        !!this.form.tasks_boards_id &&
        this.form.tasks_boards_id !== NO_BOARD_VALUE
      );
    },
    stageOptions() {
      if (!this.hasSelectedBoard) {
        return [];
      }
      const board = (this.boards || []).find(
        (item) => String(item.id) === String(this.form.tasks_boards_id),
      );
      const columns = ((board && board.columns) || [])
        .slice()
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));
      return columns.map((column) => ({
        value: String(column.id),
        label: column.name,
      }));
    },
    selectedAssignee() {
      return (this.canAssignTo || []).find(
        (user) => String(user.id) === String(this.form.assignee_id),
      );
    },
    assigneeLabel() {
      return this.selectedAssignee
        ? this.selectedAssignee.name
        : this.t("tasks-col_assignee");
    },
    priorityLabel() {
      const opt = this.priorityOptions.find(
        (item) => Number(item.value) === Number(this.form.priority),
      );
      return (opt && opt.label) || this.t("tasks-priority");
    },
    dueDateLabel() {
      return this.form.due_date || this.t("tasks-no_date");
    },
    priorityOptions() {
      return [
        { value: 3, tone: "high", label: this.t("tasks-priority_high") },
        { value: 2, tone: "medium", label: this.t("tasks-priority_medium") },
        { value: 1, tone: "low", label: this.t("tasks-priority_low") },
        {
          value: 0,
          tone: "undefined",
          label: this.t("tasks-priority_undefined"),
        },
      ];
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    initials(name) {
      if (!name) return "?";
      const parts = String(name).trim().split(/\s+/);
      const first = parts[0] ? parts[0][0] : "";
      const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
      return (first + last).toUpperCase();
    },
    priorityTone(priority) {
      const opt = this.priorityOptions.find(
        (item) => Number(item.value) === Number(priority),
      );
      return (opt && opt.tone) || "undefined";
    },
    async open(defaults = {}) {
      this.opened = true;
      this.titleError = "";
      this.createMore = false;
      this.lockClient = !!defaults.lockClient;
      this.lockedClientLabel = defaults.lockedClientLabel || "";
      this.customModalTitle = defaults.modalTitle || "";
      this.form = {
        ...emptyForm(),
        title: defaults.title || "",
        description: defaults.description || "",
        tasks_boards_id: defaults.tasks_boards_id
          ? String(defaults.tasks_boards_id)
          : NO_BOARD_VALUE,
        tasks_boards_columns_id: defaults.tasks_boards_columns_id
          ? String(defaults.tasks_boards_columns_id)
          : "",
        assignee_id: defaults.assignee_id ? String(defaults.assignee_id) : "",
        due_date: defaults.due_date || null,
        clients_id: defaults.clients_id || "",
        answers_id: defaults.answers_id || null,
        priority:
          defaults.priority === undefined || defaults.priority === null
            ? 0
            : Number(defaults.priority),
      };

      // Open first so the modal is not blocked on board/assignee fetches.
      this.$nextTick(() => {
        if (this.$refs.modal) {
          this.$refs.modal.open();
        }
      });

      await Promise.all([this.loadBoards(), this.loadAssignees()]);

      if (!this.form.assignee_id) {
        const me = this.canAssignTo.find(
          (u) => u.username === this.user.username,
        );
        if (me) {
          this.form.assignee_id = String(me.id);
        }
      }

      if (this.hasSelectedBoard && !this.form.tasks_boards_columns_id) {
        this.onBoardChange();
      }
    },
    close() {
      if (this.$refs.modal) {
        this.$refs.modal.close();
      }
    },
    onHide() {
      this.opened = false;
      this.$emit("close");
    },
    onBoardChange() {
      if (!this.hasSelectedBoard) {
        this.form.tasks_boards_columns_id = "";
        return;
      }
      const stages = this.stageOptions;
      this.form.tasks_boards_columns_id = stages.length ? stages[0].value : "";
    },
    async loadBoards() {
      try {
        const options = keviApi.getOptions("/task-boards-columns", "GET");
        const response = await this.$http(options);
        this.boards = response.body || response.data || [];
      } catch (e) {
        this.boards = [];
      }
    },
    async loadAssignees() {
      const permission =
        this.user && this.user.permissions && this.user.permissions.tasks;
      const level =
        (permission && permission.options && permission.options.level) ||
        "mine";
      try {
        // Only users are needed here — setPlace also loads branches, roles,
        // senders and WhatsApp instances and was delaying the modal.
        const options = keviApi.getOptions(
          "place/" + this.place.id + "/users",
          "GET",
        );
        const response = await this.$http(options);
        const users = response.body || [];
        const me = users.find((u) => u.username === this.user.username);
        let list = users.filter((u) => parseInt(u.active) === 1);
        if (level === "mine") {
          list = me ? [{ ...me }] : [];
        } else if (level === "branch" && me) {
          list = list.filter(
            (u) => u.places_branches_id === me.places_branches_id,
          );
        }
        this.canAssignTo = list;
      } catch (e) {
        this.canAssignTo = [];
      }
    },
    async save(toggle) {
      if (this.saving) return;
      const title = String(this.form.title || "").trim();
      if (!title) {
        this.titleError = this.t("tasks-create_task_title_required");
        return;
      }
      this.titleError = "";
      this.saving = true;

      const payload = {
        title,
        description: this.form.description || "",
        assignee_id: this.form.assignee_id || null,
        due_date: this.form.due_date || null,
        clients_id: this.form.clients_id || null,
        tasks_boards_id: this.hasSelectedBoard
          ? this.form.tasks_boards_id
          : null,
        tasks_boards_columns_id:
          this.hasSelectedBoard && this.form.tasks_boards_columns_id
            ? this.form.tasks_boards_columns_id
            : null,
        priority: Number(this.form.priority) || 0,
      };
      if (this.form.answers_id) {
        payload.answers_id = this.form.answers_id;
      }

      try {
        const options = keviApi.getOptions("/tasks", "PUT", payload);
        const response = await this.$http(options);
        const task = (response.body && response.body.task) || response.body;
        this.$store.dispatch(
          "notification/success",
          this.t("tasks-notify_task_saved"),
        );
        window.mixpanel?.track("Task created", task);
        this.$emit("saved", task);

        if (this.createMore) {
          this.form.title = "";
          this.form.description = "";
          this.titleError = "";
        } else if (typeof toggle === "function") {
          toggle();
        }
      } catch (e) {
        this.$store.dispatch(
          "notification/error",
          this.t("tasks-notify_error_load"),
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.create-task-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.create-task-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.create-task-error {
  margin: 2px 0 0;
  color: #f04438;
  font-size: 12px;
}

.create-task-description {
  ::v-deep .kv-editor {
    border: 1px solid #d0d5dd;
    border-radius: 10px;
    overflow: hidden;
    background: #fff;
  }

  ::v-deep .ProseMirror,
  ::v-deep .tiptap {
    min-height: 120px;
  }
}

.create-task-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 4px;
}

.create-task-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.create-task-chip-btn {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 999px;
  border: 1px dashed #d0d5dd;
  background: #fff;
  color: #667085;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: #98a2b3;
    background: #f9fafb;
  }

  &.has-date {
    color: var(--primary, #00542e);
    border-style: solid;
    border-color: rgba(0, 84, 46, 0.28);
    background: #edfcf3;
  }

  &.tone-high {
    color: #f04438;
    border-color: #fda29b;
  }
  &.tone-medium {
    color: #f79009;
    border-color: #fec84b;
  }
  &.tone-low {
    color: #2e90fa;
    border-color: #b2ddff;
  }
}

.create-task-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #e0f2fe;
  color: #026aa2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;

  &.mini {
    width: 22px;
    height: 22px;
    font-size: 10px;
  }
}

.create-task-menu {
  display: flex;
  flex-direction: column;
  padding: 8px;
  gap: 2px;
}

.create-task-menu-label {
  padding: 4px 8px 8px;
}

.create-task-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: left;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
  color: #101828;
  font-size: 14px;

  &:hover,
  &.active {
    background: #f2f4f7;
  }

  &.tone-high {
    color: #f04438;
  }
  &.tone-medium {
    color: #f79009;
  }
  &.tone-low {
    color: #2e90fa;
  }
}

.create-task-client {
  width: 100%;

  ::v-deep .form-control,
  ::v-deep input,
  ::v-deep .kv-input-wrapper {
    border-radius: 10px;
    min-height: 40px;
  }
}

.create-task-client-locked {
  max-width: 100%;
  align-self: flex-start;
}

.create-task-board-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.create-task-board-select {
  min-width: 140px;
  max-width: 200px;
  flex: 1;
}

.create-task-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;

  ::v-deep .kv-switch-wrapper {
    gap: 8px;

    .kv-typography {
      font-size: 13px;
    }
  }
}

.create-task-footer-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

::v-deep .kv-modal-footer {
  justify-content: stretch;
}
</style>
